import {
  isMeasure,
  Measure,
  mustBeGreaterOrEqual,
  mustBeLessOrEqual,
  mustContainInteger,
  mustContainNumber,
  Nullable,
  NullableMeasure,
  parseNumber,
  Validation,
} from '@e2m-customer-portal/shared';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, TextInputProps } from '../text-input/TextInput';

interface NullableNumberInputProps {
  /**
   * The value to display in the input.
   */
  value?: Nullable<number>;

  /**
   * Gets triggered whenever the value changes.
   * @param value
   */
  onChange?: Nullable<(value: Nullable<number>) => void>;

  /**
   * Whether the input is required to proceed.
   */
  required?: Nullable<boolean>;

  /**
   * If set, the value must be the given value or greater.
   */
  minValue?: Nullable<number>;

  /**
   * If set, the value must be the given value or lower.
   */
  maxValue?: Nullable<number>;

  /**
   * Additional rules that should be applied to this input.
   */
  rules?: Nullable<Validation<string | number>[]>;

  /**
   * Additional power level rules that should be applied to this input.
   */
  powerLevelRules?: Validation<string | number>;
}

interface NonNullableNumberInputProps {
  /**
   * The value to display in the input.
   */
  value?: Nullable<number>;

  /**
   * Gets triggered whenever the value changes.
   * @param value
   */
  onChange?: Nullable<(value: number) => void>;

  /**
   * Whether the input is required to proceed.
   */
  required: true;

  /**
   * If set, the value must be the given value or greater.
   */
  minValue?: Nullable<number>;

  /**
   * If set, the value must be the given value or lower.
   */
  maxValue?: Nullable<number>;

  /**
   * Additional rules that should be applied to this input.
   */
  rules?: Nullable<Validation<string | number>[]>;

  /**
   * Additional power level rules that should be applied to this input.
   */
  powerLevelRules?: Validation<string | number>;
}

interface NullableMeasureInputProps {
  /**
   * The value to display in the input.
   */
  value: NullableMeasure;

  /**
   * Gets triggered whenever the value changes.
   * @param value
   */
  onChange?: Nullable<(value: NullableMeasure) => void>;

  /**
   * Whether the input is required to proceed.
   */
  required?: Nullable<boolean>;

  /**
   * If set, the value must be the given value or greater.
   */
  minValue?: Nullable<Measure>;

  /**
   * If set, the value must be the given value or lower.
   */
  maxValue?: Nullable<Measure>;

  /**
   * Additional rules that should be applied to this input.
   */
  rules?: Nullable<Validation<NullableMeasure>[]>;

  /**
   * Additional power level rules that should be applied to this input.
   */
  powerLevelRules?: Validation<string | number>;
}
interface NonNullableMeasureInputProps {
  /**
   * The value to display in the input.
   */
  value: NullableMeasure;

  /**
   * Gets triggered whenever the value changes.
   * @param value
   */
  onChange?: Nullable<(value: Measure) => void>;

  /**
   * Whether the input is required to proceed.
   */
  required: true;

  /**
   * If set, the value must be the given value or greater.
   */
  minValue?: Nullable<Measure>;

  /**
   * If set, the value must be the given value or lower.
   */
  maxValue?: Nullable<Measure>;

  /**
   * Additional rules that should be applied to this input.
   */
  rules?: Nullable<Validation<NullableMeasure>[]>;

  /**
   * Additional power level rules that should be applied to this input.
   */
  powerLevelRules?: Validation<string | number>;
}
interface StandardNumberInputProps {
  name: string;
  label?: ReactNode;
  color?: Nullable<'primary'>;
  fullWidth?: boolean;
  /**
   * If set, the value must contain integer numbers, floats are not allowed.
   */
  noFractions?: Nullable<boolean>;

  /**
   * If set, the value must be a positive integer or zero.
   */
  notNegative?: Nullable<boolean>;

  /**
   * An optional boolean that will change the field styling to the warning color.
   */
  warning?: boolean;

  /**
   * Disables the input.
   */
  disabled?: boolean;

  /**
   * Hides the unit if the value is a measure. The unit is shown by default.
   */
  hideUnit?: boolean;
}

export type NumberInputProps = Omit<TextInputProps, 'value' | 'onChange' | 'required' | 'rules'> &
  StandardNumberInputProps &
  (NullableNumberInputProps | NonNullableNumberInputProps | NullableMeasureInputProps | NonNullableMeasureInputProps);

function formatValueForInput(value: Nullable<number | NullableMeasure>, language: string): string {
  if (typeof value === 'number') {
    return isNaN(value) ? '' : `${value}`;
  }

  if (value?.value == null) {
    return '';
  }

  return value.value.toLocaleString(language, {
    useGrouping: false,
  });
}

function maybeWithMeasure(
  rule: Validation<string | number> | Validation<NullableMeasure>,
  unit: Nullable<string>,
): Validation<string | number> {
  if (!unit) {
    return rule as Validation<string | number>;
  }

  return {
    ...rule,
    validate: (value) =>
      (rule as Validation<NullableMeasure>).validate({
        value: typeof value === 'string' ? parseNumber(value) : value,
        unit,
      }),
  };
}

export function NumberInput({
  name,
  value,
  onChange,
  label,
  color,
  fullWidth,
  required,
  noFractions,
  minValue,
  maxValue,
  notNegative,
  warning,
  disabled = false,
  hideUnit = false,
  rules: otherRules,
  powerLevelRules,
  ...rest
}: Readonly<NumberInputProps>): ReactElement<NumberInputProps> {
  const { t, i18n } = useTranslation('validation');
  const [text, setText] = useState(formatValueForInput(value, i18n.language));
  const unit = isMeasure(value) ? value.unit : undefined;

  const rules = useMemo(() => {
    const result: Validation<string | number>[] = [];
    if (noFractions) {
      if (notNegative) {
        result.push(
          mustBeGreaterOrEqual(0, t('COMMON_MUST_CONTAIN_POSITIVE_INTEGER')),
          mustContainInteger(t('COMMON_MUST_CONTAIN_POSITIVE_INTEGER')),
        );
      } else {
        result.push(mustContainInteger(t('COMMON_MUST_CONTAIN_INTEGER')));
      }
    } else if (notNegative) {
      result.push(
        mustBeGreaterOrEqual(0, t('COMMON_MUST_CONTAIN_POSITIVE_NUMBER')),
        mustContainNumber(t('COMMON_MUST_CONTAIN_POSITIVE_NUMBER')),
      );
    } else {
      result.push(mustContainNumber(t('COMMON_MUST_CONTAIN_NUMBER')));
    }
    if (powerLevelRules) {
      result.push(powerLevelRules);
    }
    if (minValue != null) {
      result.push(
        maybeWithMeasure(
          mustBeGreaterOrEqual(
            minValue,
            t('COMMON_MUST_BE_GREATER_OR_EQUAL', {
              value: minValue,
            }),
          ),
          unit,
        ),
      );
    }
    if (maxValue != null) {
      result.push(
        maybeWithMeasure(
          mustBeLessOrEqual(
            maxValue,
            t('COMMON_MUST_BE_LESS_OR_EQUAL', {
              value: maxValue,
            }),
          ),
          unit,
        ),
      );
    }
    if (otherRules) {
      for (const rule of otherRules) {
        result.push(maybeWithMeasure(rule, unit));
      }
    }
    return result;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notNegative, minValue, maxValue, otherRules, t, noFractions, unit]);

  const handleTextChange = useCallback(
    (changedText: string) => {
      setText(changedText);
      const currentValue = (isMeasure(value) ? value.value : value) ?? NaN;
      const changedValue = parseNumber(changedText);
      if (onChange && currentValue !== changedValue) {
        (onChange as (value: Nullable<number | NullableMeasure>) => void)(
          isMeasure(value) ? { ...value, value: changedValue } : changedValue,
        );
      }
    },
    [value, onChange],
  );

  useEffect(() => {
    if (value == null || (isMeasure(value) && value.value == null)) {
      setText('');
      return;
    }
    const currentValue = parseNumber(text);
    const changedValue = (isMeasure(value) ? value.value : value) ?? NaN;
    if (!isNaN(changedValue) && changedValue !== currentValue) {
      // Do not update the text if the value didn't change. Else, leading zeros
      // would be trimmed and any error would result in a `NaN` text.
      setText(formatValueForInput(value, i18n.language));
    }
    // `value` should be the only dependency, since the displayed text should
    // only change when the value changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const endAdornment = useMemo(() => {
    if (hideUnit || !isMeasure(value)) {
      return undefined;
    }

    return value.unit;
  }, [hideUnit, value]);

  return (
    <TextInput
      name={name}
      value={text}
      label={label}
      color={color}
      fullWidth={fullWidth}
      required={required}
      endAdornment={endAdornment}
      warning={warning}
      onChange={handleTextChange}
      disabled={disabled}
      rules={rules}
      {...rest}
    />
  );
}
